/**
 * Staff sign-in. The only unauthenticated route in the console.
 *
 * Email plus password rather than the phone-and-OTP flow the customer surfaces
 * use — staff accounts are provisioned by an owner, not self-registered.
 */

import { useState, type SubmitEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Lock, Mail, ShieldAlert } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { brandMark, brandName, brandTagline, loginBackground, wordmark, wordmarkLight } from '../lib/assets';
import { Button, Field, TextInput } from '../components/ui';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useAuth((s) => s.session);
  const signIn = useAuth((s) => s.signIn);
  const pending = useAuth((s) => s.pending);
  const toast = useUi((s) => s.toast);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Where the guard bounced us from, so sign-in returns there. */
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (session) return <Navigate to={from} replace />;

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }

    const result = await signIn(email, password);

    if (!result.ok) {
      setError(result.error);
      setPassword('');
      return;
    }

    toast('Signed in');
    navigate(from, { replace: true });
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_1.05fr]">
      {/* Editorial panel */}
      <div className="relative hidden lg:block">
        <img src={loginBackground} alt="" aria-hidden className="absolute inset-0 size-full object-cover" />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(to bottom, rgba(10,36,29,0.55) 0%, rgba(10,36,29,0.78) 55%, rgba(10,36,29,0.95) 100%)',
          }}
        />

        <div className="relative flex h-full flex-col justify-between p-10">
          <div className="flex flex-col items-start gap-3.5">
            {/* The stacked lockup: emblem centred over the 48px wordmark, 1.1x its height. */}
            <div className="flex flex-col items-center gap-[7px]">
              <img src={brandMark} alt="" aria-hidden className="h-[53px] w-auto" />
              <img src={wordmarkLight} alt={brandName} className="h-12 w-auto" />
            </div>
            <p className="text-[12.5px] tracking-wide text-white/78">{brandTagline}</p>
          </div>

          <div>
            <p className="overline text-[#c69b3f]">Operations console</p>
            <h2 className="mt-3 max-w-sm text-[30px] leading-tight font-bold tracking-[-0.025em] text-white">
              Dispatch, catalogue and care — in one place.
            </h2>
            <ul className="mt-6 flex flex-col gap-2.5">
              {[
                'Assign therapists and manage every booking',
                'Edit services, tiers and pricing live',
                'Hand out kits and uniforms by partner ID',
                'Moderate reviews and run offers',
              ].map((point) => (
                <li key={point} className="flex items-center gap-2.5 text-[13px] text-white/82">
                  <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-[#c69b3f]" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-[11.5px] text-white/45">
            Authorised personnel only. All actions are recorded in the audit trail.
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src={brandMark} alt="" aria-hidden className="h-9 w-auto" />
            <span aria-hidden className="h-7 w-px bg-[#e6e2da]" />
            <img src={wordmark} alt={brandName} className="h-9 w-auto" />
          </div>

          <span className="overline text-[#c69b3f]">Staff access</span>
          <h1 className="mt-2 text-[28px] leading-tight font-bold tracking-[-0.025em] text-[#12241e]">
            Sign in to the console
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-[#5c6d64]">
            Use the credentials issued to you. Accounts are provisioned by an owner — there is no
            self sign-up.
          </p>

          <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4" noValidate>
            <Field label="Work email" required>
              {(props) => (
                <div className="relative">
                  <Mail
                    size={15}
                    strokeWidth={2}
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[#a4b0a8]"
                  />
                  <TextInput
                    {...props}
                    type="email"
                    autoComplete="username"
                    autoFocus
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    placeholder="you@kayracare.com"
                    error={Boolean(error)}
                    className="pl-9"
                  />
                </div>
              )}
            </Field>

            <Field label="Password" required>
              {(props) => (
                <div className="relative">
                  <Lock
                    size={15}
                    strokeWidth={2}
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[#a4b0a8]"
                  />
                  <TextInput
                    {...props}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    placeholder="••••••••"
                    error={Boolean(error)}
                    className="pr-10 pl-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute top-1/2 right-2 -translate-y-1/2 rounded-lg p-1.5 text-[#71827a] transition-colors hover:bg-[#f6f4f0]"
                  >
                    {showPassword ? (
                      <EyeOff size={15} strokeWidth={2} aria-hidden />
                    ) : (
                      <Eye size={15} strokeWidth={2} aria-hidden />
                    )}
                  </button>
                </div>
              )}
            </Field>

            {error ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-xl bg-[#f8e7e4] px-3.5 py-2.5 text-[12.5px] font-semibold text-[#b4453a]"
              >
                <ShieldAlert size={14} strokeWidth={2.2} className="mt-0.5 shrink-0" aria-hidden />
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              size="md"
              fullWidth
              loading={pending}
              label={pending ? 'Signing in…' : 'Sign in'}
              icon={!pending ? <KeyRound size={15} strokeWidth={2.2} aria-hidden /> : undefined}
            />
          </form>

          <p className="mt-6 text-center text-[11.5px] leading-relaxed text-[#a4b0a8]">
            Trouble signing in? Contact an owner at{' '}
            <a href="mailto:care@kayracare.com" className="font-semibold text-[#0f332a] underline">
              care@kayracare.com
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
